import { z } from 'zod';

/** SEO constraint thresholds */
export const CONTENT_TITLE_MAX_LENGTH = 60;
export const CONTENT_META_DESCRIPTION_MIN_LENGTH = 120;
export const CONTENT_META_DESCRIPTION_MAX_LENGTH = 160;

export const FaqItemSchema = z.object({
  q: z.string().trim().min(5, 'FAQ question must be at least 5 characters.'),
  a: z.string().trim().min(10, 'FAQ answer must be at least 10 characters.'),
});
export type FaqItem = z.infer<typeof FaqItemSchema>;

export const RelatedLinkSchema = z.object({
  href: z.string().trim().min(1, 'Related link href cannot be empty.'),
  label: z.string().trim().min(1, 'Related link label cannot be empty.'),
});
export type RelatedLink = z.infer<typeof RelatedLinkSchema>;

export const ContentImageSchema = z
  .object({
    src: z.string().trim().min(1, 'Image src is required.'),
    alt: z.string().trim().min(5, 'Image alt text must be at least 5 characters.'),
    width: z.number().int().positive().default(1200),
    height: z.number().int().positive().default(630),
    aiGenerated: z.boolean().default(false),
    aiDisclosure: z.string().trim().optional(),
  })
  .refine(
    (img) => !img.aiGenerated || (typeof img.aiDisclosure === 'string' && img.aiDisclosure.length > 0),
    {
      message: 'Conspicuous AI disclosure is required when aiGenerated is true.',
      path: ['aiDisclosure'],
    },
  );
export type ContentImage = z.infer<typeof ContentImageSchema>;

export const LANDING_PAGE_TYPES = ['persona', 'use-case'] as const;
export type LandingPageType = (typeof LANDING_PAGE_TYPES)[number];
export const LandingPageTypeSchema = z.enum(LANDING_PAGE_TYPES);

export const LANDING_PREVIEW_TRACKS = ['Inbox', 'Archive', 'Trash'] as const;
export type LandingPreviewTrack = (typeof LANDING_PREVIEW_TRACKS)[number];
export const LandingPreviewTrackSchema = z.enum(LANDING_PREVIEW_TRACKS);

export const LandingPreviewEmailSchema = z.object({
  sender: z.string().trim().min(2, 'Sender must be at least 2 characters.'),
  subject: z.string().trim().min(5, 'Subject must be at least 5 characters.'),
  track: LandingPreviewTrackSchema,
  rationale: z.string().trim().min(10, 'Rationale must be at least 10 characters.'),
  timeSavedLabel: z.string().trim().optional(),
});
export type LandingPreviewEmail = z.infer<typeof LandingPreviewEmailSchema>;

export const LandingValuePropSchema = z.object({
  title: z.string().trim().min(4, 'Value prop title must be at least 4 characters.'),
  body: z.string().trim().min(15, 'Value prop body must be at least 15 characters.'),
});
export type LandingValueProp = z.infer<typeof LandingValuePropSchema>;

export const LandingEstimatorCtaSchema = z.object({
  headline: z.string().trim().min(5, 'Estimator headline must be at least 5 characters.'),
  body: z.string().trim().min(10, 'Estimator body must be at least 10 characters.'),
  buttonLabel: z.string().trim().min(3).default('Estimate your reclaimable time'),
});
export type LandingEstimatorCta = z.infer<typeof LandingEstimatorCtaSchema>;

export const LandingPageFrontmatterSchema = z.object({
  pageType: LandingPageTypeSchema,
  slug: z
    .string()
    .trim()
    .regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, 'Slug must be lowercase alphanumeric with hyphens (kebab-case).'),
  title: z
    .string()
    .trim()
    .min(5, 'Title must be at least 5 characters.')
    .max(CONTENT_TITLE_MAX_LENGTH, `Title must be at most ${CONTENT_TITLE_MAX_LENGTH} characters.`),
  metaDescription: z
    .string()
    .trim()
    .min(
      CONTENT_META_DESCRIPTION_MIN_LENGTH,
      `Meta description must be at least ${CONTENT_META_DESCRIPTION_MIN_LENGTH} characters.`,
    )
    .max(
      CONTENT_META_DESCRIPTION_MAX_LENGTH,
      `Meta description must be at most ${CONTENT_META_DESCRIPTION_MAX_LENGTH} characters.`,
    ),
  badge: z.string().trim().min(3).max(50),
  headline: z.string().trim().min(8, 'Headline must be at least 8 characters.'),
  highlight: z.string().trim().min(3).optional(),
  subhead: z.string().trim().min(20, 'Subhead must be at least 20 characters.').max(320),
  primaryCtaLabel: z.string().trim().min(3).default('Start free scan'),
  datePublished: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'datePublished must be in YYYY-MM-DD format.'),
  dateModified: z
    .string()
    .trim()
    .regex(/^\d{4}-\d{2}-\d{2}$/, 'dateModified must be in YYYY-MM-DD format.')
    .optional(),
  sampleEmails: z.array(LandingPreviewEmailSchema).min(3, 'At least 3 sample emails are required.').max(6),
  valueProps: z.array(LandingValuePropSchema).length(3, 'Exactly 3 value props are required.'),
  estimatorCta: LandingEstimatorCtaSchema.optional(),
  faqs: z.array(FaqItemSchema).min(3, 'At least 3 FAQs are required.'),
  relatedLinks: z.array(RelatedLinkSchema).min(2, 'At least 2 related links are required.'),
  image: ContentImageSchema.optional(),
});

export type LandingPageFrontmatter = z.infer<typeof LandingPageFrontmatterSchema>;
export type LandingPageFrontmatterInput = z.input<typeof LandingPageFrontmatterSchema>;
